import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Popover,
    Paper,
    Typography,
    Button,
    InputAdornment,
    ButtonBase,
    IconButton
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const DateRangePicker = ({
    dateRange,
    setDateRange,
    placeholder = "Select date range",
    maxDate = new Date(),
    disabled = false,
    type
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [startDate, setStartDate] = useState(dateRange[0]);
    const [endDate, setEndDate] = useState(dateRange[1]);
    const [hoverDate, setHoverDate] = useState(null);
    const [isSelectingEnd, setIsSelectingEnd] = useState(false);

    const open = Boolean(anchorEl);

    useEffect(() => {
        setStartDate(dateRange[0]);
        setEndDate(dateRange[1]);
    }, [dateRange]);

    const handleClick = (event) => {
        if (!disabled) {
            setAnchorEl(event.currentTarget);
            setCurrentMonth(dateRange[0] || new Date());
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsSelectingEnd(false);
        setHoverDate(null);
    };

    const handleApply = () => {
        if (startDate && endDate) {
            setDateRange([startDate, endDate]);
        }
        handleClose();
    };

    const handleClear = () => {
        setStartDate(null);
        setEndDate(null);
        setDateRange([null, null]);
        handleClose();
    };

    const handleDateClick = (date) => {
        if (!startDate || (startDate && endDate) || date < startDate) {
            setStartDate(date);
            setEndDate(null);
            setIsSelectingEnd(true);
        } else if (startDate && !endDate) {
            setEndDate(date);
            setIsSelectingEnd(false);
        }
    };

    const handleDateHover = (date) => {
        if (isSelectingEnd && startDate && !endDate) {
            setHoverDate(date);
        }
    };

    const formatDisplayValue = () => {
        if (startDate && endDate) {
            const formatDate = (date) => {
                const day = String(date.getDate()).padStart(2, '0');
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
            };

            if (startDate.getTime() === endDate.getTime()) {
                return formatDate(startDate);
            }
            return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        }
        return '';
    };

    const navigateMonth = (direction) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + direction);
        setCurrentMonth(newMonth);
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Add empty cells for days before the month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Add days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }

        return days;
    };

    const isDateInRange = (date) => {
        if (!date) return false;

        const checkStart = startDate;
        const checkEnd = endDate || hoverDate;

        if (checkStart && checkEnd) {
            return date > checkStart && date < checkEnd;
        }
        return false;
    };

    const isDateRangeEndpoint = (date) => {
        if (!date) return false;
        return (startDate && date.getTime() === startDate.getTime()) ||
            (endDate && date.getTime() === endDate.getTime());
    };

    const isDateDisabled = (date) => {
        if (!date) return false;
        return date > maxDate;
    };

    const isWeekend = (date) => {
        if (!date) return false;
        const day = date.getDay();
        return day === 0 || day === 6; // Sunday = 0, Saturday = 6
    };

    const monthYear = currentMonth.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
    });

    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const days = getDaysInMonth(currentMonth);

    return (
        <Box sx={{ width: type === "delivery" ? "300px" : '320px' }}>
            <TextField
                fullWidth
                value={formatDisplayValue()}
                placeholder={placeholder}
                variant="outlined"
                disabled={disabled}
                onClick={handleClick}
                InputProps={{
                    readOnly: true,
                    style: {
                        cursor: disabled ? 'default' : 'pointer',
                        height: type === "delivery" ? "40px" : '55px'
                    },
                    endAdornment: (
                        <InputAdornment position="end">
                            <CalendarTodayIcon
                                sx={{
                                    color: disabled ? 'text.disabled' : 'text.secondary',
                                    cursor: disabled ? 'default' : 'pointer'
                                }}
                            />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        cursor: disabled ? 'default' : 'pointer',
                        '&:hover fieldset': {
                            borderColor: disabled ? 'rgba(0, 0, 0, 0.23)' : 'primary.main',
                        },
                    },
                    '& .MuiInputBase-input': {
                        cursor: disabled ? 'default' : 'pointer',
                    }
                }}
            />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    elevation: 8,
                    sx: {
                        mt: 1,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                    }
                }}
            >
                <Paper sx={{ width: 320, p: 2 }}>
                    {/* Month navigation */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                        px: 1
                    }}>
                        <IconButton
                            size="small"
                            onClick={() => navigateMonth(-1)}
                            sx={{
                                color: 'primary.main',
                                '&:hover': { backgroundColor: 'primary.light' }
                            }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: '1rem'
                            }}
                        >
                            {monthYear}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => navigateMonth(1)}
                            sx={{
                                color: 'primary.main',
                                '&:hover': { backgroundColor: 'primary.light' }
                            }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>

                    {/* Days of week header */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: 0.5,
                        mb: 1
                    }}>
                        {weekdays.map((day, index) => (
                            <Typography
                                key={day}
                                variant="caption"
                                sx={{
                                    textAlign: 'center',
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.75,
                                    py: 0.5,
                                }}
                            >
                                {day}
                            </Typography>
                        ))}
                    </Box>

                    {/* Calendar grid */}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: 0.5,
                        mb: 2
                    }}>
                        {days.map((day, index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto'
                                }}
                            >
                                {day && (
                                    <ButtonBase
                                        onClick={() => handleDateClick(day)}
                                        onMouseEnter={() => handleDateHover(day)}
                                        disabled={isDateDisabled(day)}
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '50%',
                                            fontSize: '0.875rem',
                                            fontWeight: 400,
                                            transition: 'all 0.2s ease',
                                            color: isDateRangeEndpoint(day)
                                                ? 'primary.contrastText'
                                                : isWeekend(day)
                                                    ? 'text.disabled'
                                                    : 'text.primary',
                                            backgroundColor: isDateRangeEndpoint(day)
                                                ? 'primary.main'
                                                : isDateInRange(day)
                                                    ? 'rgba(244, 67, 54, 0.12)'
                                                    : 'transparent',
                                            '&:hover': {
                                                backgroundColor: isDateRangeEndpoint(day)
                                                    ? 'primary.dark'
                                                    : isDateInRange(day)
                                                        ? 'rgba(244, 67, 54, 0.2)'
                                                        : 'rgba(0, 0, 0, 0.04)',
                                            },
                                            '&:disabled': {
                                                color: 'text.disabled',
                                                cursor: 'not-allowed',
                                                opacity: 0.5,
                                            },
                                            '&:focus': {
                                                outline: '2px solid',
                                                outlineColor: 'primary.main',
                                                outlineOffset: '2px',
                                            }
                                        }}
                                    >
                                        {day.getDate()}
                                    </ButtonBase>
                                )}
                            </Box>
                        ))}
                    </Box>

                    {/* Action buttons */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        pt: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Button
                            variant="text"
                            size="small"
                            onClick={handleClear}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                color: 'text.secondary'
                            }}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleApply}
                            disabled={!startDate || !endDate}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                boxShadow: 'none',
                                '&:hover': {
                                    boxShadow: 1,
                                }
                            }}
                        >
                            Apply
                        </Button>
                    </Box>
                </Paper>
            </Popover>
        </Box>
    );
};

export default DateRangePicker;